import type { NavigateFunction } from "react-router-dom";
import type { Notification } from "@/types/notification";
import type { QROrderFollowUpEventMeta, TableAssistEventMeta } from "@/types/qr-orders";

/**
 * Notifications after which staff must go to the table: an auto-accepted or stuck
 * QR order (send the items, assign a waiter), or a guest calling a waiter or
 * asking for the bill from the QR menu (table_assist).
 */
export const QR_FOLLOW_UP_TYPES = new Set(["qr_order_auto_accepted", "qr_order_needs_attention", "table_assist"]);

export interface QRFollowUpTable {
  id: string;
  number: string;
  orderId: string | null;
}

/** The table a QR follow-up notification is about, or null for any other notification. */
export function qrFollowUpTarget(n: Pick<Notification, "notification_type" | "metadata">): QRFollowUpTable | null {
  if (!QR_FOLLOW_UP_TYPES.has(n.notification_type)) return null;
  const meta = (n.metadata ?? {}) as Partial<QROrderFollowUpEventMeta>;
  if (!meta.table_id) return null;
  return { id: meta.table_id, number: meta.table_number ?? "", orderId: meta.order_id ?? null };
}

/**
 * At most this many order follow-up toasts (auto-accepted, needs attention) at once,
 * and separately at most this many guest-request toasts: the oldest of its kind goes.
 * Separate caps, so a burst of guest requests never pushes out an order staff still
 * have to act on (and the other way round).
 */
export const MAX_QR_FOLLOW_UP_TOASTS = 3;

const ASSIST_TOAST_PREFIX = "table-assist:";

/** Open follow-up toasts of each kind, oldest first. */
const followUpToasts = { order: [] as string[], assist: [] as string[] };

const kindOf = (id: string): keyof typeof followUpToasts => (id.startsWith(ASSIST_TOAST_PREFIX) ? "assist" : "order");

/**
 * The toast id of a follow-up; a newer notification with the same id replaces the toast.
 * - An auto-accepted or stuck order: `qr-follow-up:{table}`, one per table.
 * - A guest request: `table-assist:{table}:{call|bill}`, one per table and request
 *   type. It never replaces the table's order follow-up: staff still have to act on that.
 */
export function followUpToastId(n: Pick<Notification, "notification_type" | "metadata">, table: QRFollowUpTable): string {
  if (n.notification_type === "table_assist") {
    const type = ((n.metadata ?? {}) as Partial<TableAssistEventMeta>).assist_type ?? "call";
    return `${ASSIST_TOAST_PREFIX}${table.id}:${type}`;
  }
  return `qr-follow-up:${table.id}`;
}

/** A follow-up toast went away (dismissed, closed). */
export function forgetFollowUpToast(id: string) {
  const list = followUpToasts[kindOf(id)];
  const i = list.indexOf(id);
  if (i >= 0) list.splice(i, 1);
}

/** A follow-up toast is shown (or replaced): returns the oldest of its kind to dismiss to keep the cap. */
export function trackFollowUpToast(id: string): string[] {
  forgetFollowUpToast(id);
  const list = followUpToasts[kindOf(id)];
  list.push(id);
  return list.splice(0, Math.max(0, list.length - MAX_QR_FOLLOW_UP_TOASTS));
}

/**
 * Forget every follow-up toast, order and guest-request ones alike (a new person
 * signed in; theirs start afresh).
 */
export function resetQrFollowUpToasts() {
  followUpToasts.order.length = 0;
  followUpToasts.assist.length = 0;
}

/** Test hook. */
export const resetQrFollowUpToastsForTests = resetQrFollowUpToasts;

/** Orders, on that table's order (Orders also follows this while already open). */
export function openTableInOrders(navigate: NavigateFunction, table: QRFollowUpTable) {
  navigate("/orders", {
    state: { selectedTable: { id: table.id, number: table.number, orderId: table.orderId } },
  });
}
